import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import { isEqual } from 'lodash-es';

import { IStoreAddress } from '@rbi-ctg/store';
import { usePrevious } from 'hooks/use-previous';
import { LaunchDarklyFlag, useFlag, useLDContext } from 'state/launchdarkly';
import { useServiceModeContext } from 'state/service-mode';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { initStore } from 'utils/preload-store';
import { remapStore } from 'utils/restaurant';

import {
  IPreloadedStoreState,
  IStoreContext,
  IStoreHook,
  IUseStore,
  Store,
  StoreProxy,
  StoreState
} from './types';
import { resetLastTimeStoreUpdated } from './use-store-utils';

const preloadedStoreInfo = (): IPreloadedStoreState =>
  LocalStorage.getItem(StorageKeys.STORE) || ({} as IPreloadedStoreState);

export const initialStoreState: StoreState = {
  hasSelection: false,
  selectedUnavailableStoreName: '',
  physicalAddress: {} as IStoreAddress,
  chaseMerchantId: '',
  selectedUnavailableStoreNumber: null
};

/**
 * Hook used for store selection 1.0
 */
export const useStore = (): IUseStore => {
  const { updateUserStore } = useLDContext();
  const { serviceMode } = useServiceModeContext();
  const preloaded = useMemo(() => preloadedStoreInfo(), []);

  // Temporary flag
  // Allows us to select a restaurant when it is unavailable
  const showUnavailableRestaurant = useFlag(
    LaunchDarklyFlag.DISPLAY_SELECTED_RESTAURANT_INFORMATION_WHEN_RESTAURANT_UNAVAILABLE
  );

  /** Explicit state of provider */
  const [_storeState, _setStoreState] = useState<StoreState>(initStore(preloaded, serviceMode));
  const storeState = useMemo(() => remapStore(_storeState), [_storeState]);
  const setStoreState = useCallback<Dispatch<SetStateAction<StoreState>>>(
    newStore =>
      _setStoreState(s => {
        const value = typeof newStore === 'function' ? newStore(s) : newStore;

        if (isEqual(value, s)) {
          return s;
        }

        return value;
      }),
    []
  );

  /** Change store back to initial state (excluding prefetch) */
  const resetStoreState = useCallback<IStoreContext['resetStore']>(
    () => setStoreState(initialStoreState),
    [setStoreState]
  );

  const selectUnavailableStore = useCallback(
    ({
      name,
      number,
      physicalAddress
    }: {
      name?: string | null;
      number?: string | null;
      physicalAddress?: IStoreAddress | null;
    }) => {
      setStoreState({
        ...initialStoreState,
        selectedUnavailableStoreName: name ?? initialStoreState.selectedUnavailableStoreName,
        physicalAddress: physicalAddress ?? initialStoreState.physicalAddress,
        selectedUnavailableStoreNumber: number ?? initialStoreState.selectedUnavailableStoreNumber
      });
    },
    [setStoreState]
  );

  const onConfirmStoreChange: IStoreHook['onConfirmStoreChange'] = useCallback(
    async ({ newStore, callback }) => {
      setStoreState({
        hasSelection: true,
        ...newStore
      });

      resetLastTimeStoreUpdated();
      if (typeof callback === 'function') {
        await callback();
      }
    },
    [setStoreState]
  );

  const previousShowUnavailableRestaurant = usePrevious(showUnavailableRestaurant);
  useEffect(() => {
    if (
      // Reset store when the LD flag changes to false
      previousShowUnavailableRestaurant !== showUnavailableRestaurant &&
      !showUnavailableRestaurant &&
      // And unavailable store is selected
      'selectedUnavailableStoreNumber' in storeState &&
      !!storeState.selectedUnavailableStoreNumber
    ) {
      resetStoreState();
    }
  }, [previousShowUnavailableRestaurant, resetStoreState, showUnavailableRestaurant, storeState]);

  useEffect(() => {
    LocalStorage.setItem(StorageKeys.STORE, {
      store: storeState
    });
    // Update the user store in LD
    updateUserStore(storeState as Store);
  }, [storeState, updateUserStore]);

  const storeHasPhysicalAddress =
    storeState.physicalAddress && !!Object.keys(storeState.physicalAddress).length;
  /** An object similar to the {@link:IStore} type but all properties can be null */
  const storeProxy: StoreProxy = useMemo(
    () => ({
      frontCounterClosed: storeState.hasSelection ? storeState.frontCounterClosed : null,
      hasTakeOut: storeState.hasSelection ? storeState.hasTakeOut : null,
      hasDineIn: storeState.hasSelection ? storeState.hasDineIn : null,
      _id: storeState.hasSelection ? storeState._id : null,
      deliveryHours: storeState.hasSelection ? storeState.deliveryHours : null,
      diningRoomHours: storeState.hasSelection ? storeState.diningRoomHours : null,
      drinkStationType: storeState.hasSelection ? storeState.drinkStationType : null,
      driveThruHours: storeState.hasSelection ? storeState.driveThruHours : null,
      driveThruLaneType: storeState.hasSelection ? storeState.driveThruLaneType : null,
      curbsideHours: storeState.hasSelection ? storeState.curbsideHours : null,
      email: storeState.hasSelection ? storeState.email : null,
      environment: storeState.hasSelection ? storeState.environment : null,
      franchiseGroupId: storeState.hasSelection ? storeState.franchiseGroupId : null,
      franchiseGroupName: storeState.hasSelection ? storeState.franchiseGroupName : null,
      hasBreakfast: storeState.hasSelection ? storeState.hasBreakfast : null,
      hasBurgersForBreakfast: storeState.hasSelection ? storeState.hasBurgersForBreakfast : null,
      hasCurbside: storeState.hasSelection ? storeState.hasCurbside : null,
      hasCatering: storeState.hasSelection ? storeState.hasCatering : null,
      hasDelivery: storeState.hasSelection ? storeState.hasDelivery : null,
      hasDriveThru: storeState.hasSelection ? storeState.hasDriveThru : null,
      hasMobileOrdering: storeState.hasSelection ? storeState.hasMobileOrdering : null,
      hasParking: storeState.hasSelection ? storeState.hasParking : null,
      hasPlayground: storeState.hasSelection ? storeState.hasPlayground : null,
      hasWifi: storeState.hasSelection ? storeState.hasWifi : null,
      isDarkKitchen: storeState.hasSelection ? storeState.isDarkKitchen : null,
      latitude: storeState.hasSelection ? storeState.latitude : null,
      longitude: storeState.hasSelection ? storeState.longitude : null,
      mobileOrderingStatus: storeState.hasSelection ? storeState.mobileOrderingStatus : null,
      parkingType: storeState.hasSelection ? storeState.parkingType : null,
      phoneNumber: storeState.hasSelection ? storeState.phoneNumber : null,
      playgroundType: storeState.hasSelection ? storeState.playgroundType : null,
      pos: storeState.hasSelection ? storeState.pos : null,
      posRestaurantId: storeState.hasSelection ? storeState.posRestaurantId : null,
      restaurantImage: storeState.hasSelection ? storeState.restaurantImage : null,
      restaurantPosData: storeState.hasSelection ? storeState.restaurantPosData : null,
      status: storeState.hasSelection ? storeState.status : null,
      chaseMerchantId: storeState.hasSelection ? storeState.chaseMerchantId : null,
      distanceInMiles: storeState.hasSelection ? storeState.distanceInMiles : null,

      // These don't depend on `hasSelection`
      name: storeState.hasSelection
        ? storeState.name
        : storeState.selectedUnavailableStoreName || null,
      number: storeState.hasSelection
        ? storeState.number
        : storeState.selectedUnavailableStoreNumber ?? null,
      physicalAddress: storeHasPhysicalAddress ? storeState.physicalAddress : null
    }),
    [storeState, storeHasPhysicalAddress]
  );

  return useMemo(
    () => ({
      resetStore: resetStoreState,
      setStore: setStoreState,
      store: storeState,
      selectUnavailableStore,
      onConfirmStoreChange,
      storeProxy
    }),
    [
      onConfirmStoreChange,
      resetStoreState,
      selectUnavailableStore,
      setStoreState,
      storeProxy,
      storeState
    ]
  );
};
